import { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Upload, X } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { useBuilder } from '@/contexts/BuilderContext';

interface OperatorSettingsProps {
  chatbotId: string;
  operatorName?: string | null;
  operatorAvatarUrl?: string | null;
  reportUnsavedChanges?: (isDirty: boolean) => void;
}

export const OperatorSettings = ({ 
  chatbotId,
  operatorName = "", 
  operatorAvatarUrl,
  reportUnsavedChanges
}: OperatorSettingsProps) => {
  const [name, setName] = useState(operatorName || "");
  const [isHovering, setIsHovering] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { updateBasicInfo, setPendingOperatorAvatar, setRemoveOperatorAvatar, state } = useBuilder();
  const { toast } = useToast();

  useEffect(() => {
    setName(operatorName || "");
  }, [operatorName]);

  // Create preview URL when pending file changes
  useEffect(() => {
    // If avatar is marked for removal, clear preview immediately
    if (state.removeOperatorAvatarOnSave) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }

    if (state.pendingOperatorAvatarFile) {
      // Create preview URL for the new file
      const url = URL.createObjectURL(state.pendingOperatorAvatarFile);
      setPreviewUrl(url);
      previewUrlRef.current = url;
      
      // Cleanup function to revoke the URL when component unmounts or file changes
      return () => {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      };
    } else {
      // Clear preview if no pending file
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
    }
  }, [state.pendingOperatorAvatarFile, state.removeOperatorAvatarOnSave]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  // Handle operator name change - only update local state
  const handleNameChange = (newName: string) => {
    setName(newName);
    // Update through context (will be saved via main Save Changes button)
    updateBasicInfo({ operator_name: newName || null });
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (!file) return;
    
    // Check file type
    if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload an image file (JPEG, PNG, GIF, WEBP)",
      });
      return;
    }
    
    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "File size should be less than 5MB",
      });
      return;
    }
    
    // Store file in context for later upload (will be uploaded via main Save Changes button)
    setPendingOperatorAvatar(file);
    setRemoveOperatorAvatar(false);
    
    // Mark as dirty
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
    
    toast({
      title: "Avatar selected",
      description: "Save changes to upload the operator avatar.",
    });
  };
  
  const handleDeleteAvatar = () => {
    // Clear preview immediately (synchronous)
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    
    // Mark avatar for removal (will be removed via main Save Changes button)
    setPendingOperatorAvatar(null);
    setRemoveOperatorAvatar(true);
    
    // Update through context
    updateBasicInfo({ operator_avatar_url: null });
    
    // Mark as dirty
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
    
    toast({
      title: "Avatar marked for removal",
      description: "Save changes to remove the operator avatar.",
    });
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="flex flex-col space-y-4 py-4">
      <div className="flex flex-col space-y-2">
        <Label htmlFor="operator-name">Operator Name</Label>
        <Input
          id="operator-name"
          placeholder="Enter operator name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
        />
        <p className="text-sm text-gray-500">
          This name will be displayed as the chat operator in conversations
        </p>
      </div>
      <div className="flex flex-col space-y-2">
        <Label>Operator Avatar</Label>
        <div className="flex items-center space-x-4">
          <div 
            className="relative cursor-pointer"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <label htmlFor="operator-avatar-upload" className="cursor-pointer block">
              <Avatar 
                className="h-16 w-16"
                key={`${state.removeOperatorAvatarOnSave}-${!!previewUrl}-${operatorAvatarUrl || 'no-avatar'}`}
              >
                {(() => {
                  // Priority 1: If avatar is marked for removal, ALWAYS show fallback (highest priority)
                  if (state.removeOperatorAvatarOnSave) {
                    return <AvatarFallback>{getInitials(name || "Operator")}</AvatarFallback>;
                  }
                  // Priority 2: If there's a pending file and preview URL exists, show preview
                  if (state.pendingOperatorAvatarFile && previewUrl) {
                    return <AvatarImage src={previewUrl} alt={name} />;
                  }
                  // Priority 3: If there's a current avatar URL and no pending file, show it
                  if (operatorAvatarUrl && !state.pendingOperatorAvatarFile) {
                    return <AvatarImage src={operatorAvatarUrl} alt={name} />;
                  }
                  // Priority 4: Default to fallback with initials
                  return <AvatarFallback>{getInitials(name || "Operator")}</AvatarFallback>;
                })()}
              </Avatar>
              
              {isHovering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-white"
                    disabled={isUploading}
                    onClick={(e) => {
                      e.preventDefault();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    {isUploading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <Upload size={24} />
                    )}
                  </Button>
                  {/* Show delete button only if there's something to delete (current avatar or preview) and not already marked for removal */}
                  {(() => {
                    const hasAvatarToDelete = !state.removeOperatorAvatarOnSave && (operatorAvatarUrl || previewUrl);
                    return hasAvatarToDelete && !isUploading;
                  })() && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white absolute bottom-0 right-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteAvatar();
                      }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              )}
            </label>
            <input
              ref={fileInputRef}
              id="operator-avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>
          
          <div className="flex-1">
            <p className="text-sm text-gray-500">
              Upload an avatar for the chat operator. This will be displayed in the chat header.
            </p>
          </div>
        </div>
      </div>
      
      {state.pendingOperatorAvatarFile && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Avatar file selected. Save changes to upload.
          </p>
        </div>
      )}
      
      {state.removeOperatorAvatarOnSave && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            Avatar marked for removal. Save changes to apply.
          </p>
        </div>
      )}
    </div>
  );
};
